import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { usePipedriveStore } from '../..';
import { ILeadFilterSettings } from '../../interfaces/IFilterSettings';
import { LeadOwnershipType, LeadStatus } from '../../interfaces/ILead';
import { leadOwnershipTypeToString, leadStatusToString } from '../../utils/enumUtils';
import { formatDateForInput, parseDate } from '../../utils/utils';
import styles from './GlobalFilters.module.css';

const locationGradeOptions = ["A", "B", "C", "D", "F"];
const propertyGradeOptions = ["A", "B", "C", "D", "F", "N/A"];

function GlobalFiltersLeadsTab({ leadFiltersState, setLeadFiltersState }: IGlobalFiltersLeadsTabProps) {
    const pipedriveStore = usePipedriveStore();
    const labelOptions = useMemo(() => computed(() => pipedriveStore.labelOptions), []).get();
    return (
        <div className={styles.columns}>
            <div className={styles.column}>
                <label className={styles.label}>
                    LOT COVERAGE
                    <div>
                        <input type="number" className={styles.numberInput} placeholder="Enter min" value={leadFiltersState.minLotCoverage || ""} onChange={e => setLeadFiltersState({
                            ...leadFiltersState,
                            minLotCoverage: e.target.valueAsNumber || null,
                        })} />
                        &nbsp;-&nbsp;
                        <input type="number" className={styles.numberInput} placeholder="Enter max" value={leadFiltersState.maxLotCoverage || ""} onChange={e => setLeadFiltersState({
                            ...leadFiltersState,
                            maxLotCoverage: e.target.valueAsNumber || null,
                        })} />
                    </div>
                </label>
                <label className={styles.label}>
                    BUILDING SIZE
                    <div>
                        <input type="number" className={styles.numberInput} placeholder="Enter min" value={leadFiltersState.minBuildingSize || ""} onChange={e => setLeadFiltersState({
                            ...leadFiltersState,
                            minBuildingSize: e.target.valueAsNumber || null,
                        })} />
                        &nbsp;-&nbsp;
                        <input type="number" className={styles.numberInput} placeholder="Enter max" value={leadFiltersState.maxBuildingSize || ""} onChange={e => setLeadFiltersState({
                            ...leadFiltersState,
                            maxBuildingSize: e.target.valueAsNumber || null,
                        })} />
                    </div>
                </label>
                <label className={styles.label}>
                    LAND SIZE
                    <div>
                        <input type="number" className={styles.numberInput} placeholder="Enter min" value={leadFiltersState.minLandSize || ""} onChange={e => setLeadFiltersState({
                            ...leadFiltersState,
                            minLandSize: e.target.valueAsNumber || null,
                        })} />
                        &nbsp;-&nbsp;
                        <input type="number" className={styles.numberInput} placeholder="Enter max" value={leadFiltersState.maxLandSize || ""} onChange={e => setLeadFiltersState({
                            ...leadFiltersState,
                            maxLandSize: e.target.valueAsNumber || null,
                        })} />
                    </div>
                </label>
                <label className={styles.label}>
                    USABLE LAND SIZE
                    <div>
                        <input type="number" className={styles.numberInput} placeholder="Enter min" value={leadFiltersState.minUsableArea || ""} onChange={e => setLeadFiltersState({
                            ...leadFiltersState,
                            minUsableArea: e.target.valueAsNumber || null,
                        })} />
                        &nbsp;-&nbsp;
                        <input type="number" className={styles.numberInput} placeholder="Enter max" value={leadFiltersState.maxUsableArea || ""} onChange={e => setLeadFiltersState({
                            ...leadFiltersState,
                            maxUsableArea: e.target.valueAsNumber || null,
                        })} />
                    </div>
                </label>
                <label className={styles.label}>
                    KEYWORD
                    <input className={styles.textInput} value={leadFiltersState.keyword} onChange={e => setLeadFiltersState({
                        ...leadFiltersState,
                        keyword: e.target.value,
                    })} />
                </label>
                <label className={styles.label}>
                    DATE MODIFIED
                    <div>
                        <input type="date" className={styles.numberInput} value={formatDateForInput(leadFiltersState.minDateModified || undefined)} onChange={e => setLeadFiltersState({
                            ...leadFiltersState,
                            minDateModified: parseDate(e.target.value) || null,
                        })} />
                        &nbsp;-&nbsp;
                        <input type="date" className={styles.numberInput} value={formatDateForInput(leadFiltersState.maxDateModified || undefined) || ""} onChange={e => setLeadFiltersState({
                            ...leadFiltersState,
                            maxDateModified: parseDate(e.target.value) || null,
                        })} />
                    </div>
                </label>
            </div>
            <div className={styles.column}>
                <label className={styles.label}>
                    STATUS
                    <div className={styles.tags}>
                        {[LeadStatus.OpenLead, LeadStatus.ActiveConversations, LeadStatus.OIPass, LeadStatus.SellerPass].map(status => (
                            <div
                                key={status}
                                className={leadFiltersState.statuses.includes(status) ? styles.selectedTag : styles.tag}
                                onClick={() => setLeadFiltersState({
                                    ...leadFiltersState,
                                    statuses: !leadFiltersState.statuses.includes(status) ? [
                                        ...leadFiltersState.statuses,
                                        status,
                                    ] : leadFiltersState.statuses.filter(s => s !== status),
                                })}
                            >
                                {leadStatusToString(status)}
                            </div>
                        ))}
                    </div>
                </label>
                <label className={styles.label}>
                    REMINDER SET
                    <div className={styles.tags}>
                        <div
                            className={leadFiltersState.showWithReminder ? styles.selectedTag : styles.tag}
                            onClick={() => setLeadFiltersState({
                                ...leadFiltersState,
                                showWithReminder: !leadFiltersState.showWithReminder,
                            })}
                        >
                            Yes
                        </div>
                        <div
                            className={leadFiltersState.showWithoutReminder ? styles.selectedTag : styles.tag}
                            onClick={() => setLeadFiltersState({
                                ...leadFiltersState,
                                showWithoutReminder: !leadFiltersState.showWithoutReminder,
                            })}
                        >
                            No
                        </div>
                    </div>
                </label>
                <label className={styles.label}>
                    LABELS
                    <div className={styles.tags}>
                        {labelOptions.map(o => (
                            <div
                                key={o.id}
                                className={leadFiltersState.labelIds.includes(o.id) ? styles.selectedTag : styles.tag}
                                onClick={() => setLeadFiltersState({
                                    ...leadFiltersState,
                                    labelIds: !leadFiltersState.labelIds.includes(o.id) ? [
                                        ...leadFiltersState.labelIds,
                                        o.id,
                                    ] : leadFiltersState.labelIds.filter(id => id !== o.id),
                                })}
                            >
                                {o.label}
                            </div>
                        ))}
                    </div>
                </label>
                <label className={styles.label}>
                    LOCATION GRADE
                    <div className={styles.tags}>
                        {locationGradeOptions.map(grade => (
                            <div
                                key={grade}
                                className={leadFiltersState.locationGrades.includes(grade) ? styles.selectedTag : styles.tag}
                                onClick={() => setLeadFiltersState({
                                    ...leadFiltersState,
                                    locationGrades: !leadFiltersState.locationGrades.includes(grade) ? [
                                        ...leadFiltersState.locationGrades,
                                        grade,
                                    ] : leadFiltersState.locationGrades.filter(g => g !== grade),
                                })}
                            >
                                {grade}
                            </div>
                        ))}
                    </div>
                </label>
                <label className={styles.label}>
                    PROPERTY GRADE
                    <div className={styles.tags}>
                        {propertyGradeOptions.map(grade => (
                            <div
                                key={grade}
                                className={leadFiltersState.propertyGrades.includes(grade) ? styles.selectedTag : styles.tag}
                                onClick={() => setLeadFiltersState({
                                    ...leadFiltersState,
                                    propertyGrades: !leadFiltersState.propertyGrades.includes(grade) ? [
                                        ...leadFiltersState.propertyGrades,
                                        grade,
                                    ] : leadFiltersState.propertyGrades.filter(g => g !== grade),
                                })}
                            >
                                {grade}
                            </div>
                        ))}
                    </div>
                </label>
                <label className={styles.label}>
                    OWNERSHIP TYPE
                    <div className={styles.tags}>
                        {Object.values(LeadOwnershipType).map(ownershipType => (
                            <div
                                key={ownershipType}
                                className={leadFiltersState.ownershipTypes.includes(ownershipType) ? styles.selectedTag : styles.tag}
                                onClick={() => setLeadFiltersState({
                                    ...leadFiltersState,
                                    ownershipTypes: !leadFiltersState.ownershipTypes.includes(ownershipType) ? [
                                        ...leadFiltersState.ownershipTypes,
                                        ownershipType,
                                    ] : leadFiltersState.ownershipTypes.filter(ot => ot !== ownershipType),
                                })}
                            >
                                {leadOwnershipTypeToString(ownershipType)}
                            </div>
                        ))}
                    </div>
                </label>
            </div>
        </div>
    );
};

export default observer(GlobalFiltersLeadsTab);

interface IGlobalFiltersLeadsTabProps {
    leadFiltersState: ILeadFilterSettings;
    setLeadFiltersState: (value: ILeadFilterSettings) => void;
}